import React from 'react';
import { getScoreColor } from '../../safecare/model/successGaugeCalculator';
import './SuccessGauge.css';

/**
 * Success Gauge Component
 * Displays 0-10 composite score with breakdown
 */
export default function SuccessGauge({ successGauge }) {
    if (!successGauge) return null;

    const { overall, scores, rating } = successGauge;

    // Safety check for NaN
    const safeOverall = Number.isFinite(overall) ? overall : 0;
    const color = getScoreColor(safeOverall);

    // Calculate rotation for gauge needle (0-10 maps to -90 to 90 degrees)
    const rotation = (safeOverall / 10) * 180 - 90;

    return (
        <div className="success-gauge-container">
            <div className="gauge-left-panel">
                <div className="gauge-header">
                    <h3>Overall Success Rating</h3>
                    <div className="gauge-rating-row">
                        <div className="gauge-rating" style={{ color }}>
                            {safeOverall.toFixed(1)} <span className="gauge-max">/ 10</span>
                        </div>
                        <div className="gauge-label">{rating}</div>
                    </div>
                </div>

                {/* Semi-circle gauge */}
                <div className="gauge-visual">
                    <svg viewBox="0 0 200 110" className="gauge-svg">
                        {/* Background arc */}
                        <path
                            d="M 20 100 A 80 80 0 0 1 180 100"
                            fill="none"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="12"
                            strokeLinecap="round"
                        />

                        {/* Segments: red (bad) → amber → green (good) */}
                        <path
                            d="M 20 100 A 80 80 0 0 1 60 40"
                            fill="none"
                            stroke="#dc2626"
                            strokeWidth="12"
                            strokeLinecap="round"
                            opacity="0.35"
                        />
                        <path
                            d="M 60 40 A 80 80 0 0 1 100 20"
                            fill="none"
                            stroke="#d97706"
                            strokeWidth="12"
                            strokeLinecap="round"
                            opacity="0.35"
                        />
                        <path
                            d="M 100 20 A 80 80 0 0 1 140 40"
                            fill="none"
                            stroke="#16a34a"
                            strokeWidth="12"
                            strokeLinecap="round"
                            opacity="0.4"
                        />
                        <path
                            d="M 140 40 A 80 80 0 0 1 180 100"
                            fill="none"
                            stroke="#15803d"
                            strokeWidth="12"
                            strokeLinecap="round"
                            opacity="0.45"
                        />

                        {/* Needle */}
                        <g transform={`rotate(${rotation} 100 100)`}>
                            <line
                                x1="100"
                                y1="100"
                                x2="100"
                                y2="35"
                                stroke={color}
                                strokeWidth="4"
                                strokeLinecap="round"
                            />
                            <circle cx="100" cy="100" r="4" fill={color} />
                        </g>
                    </svg>
                </div>
            </div>

            {/* Score breakdown */}
            <div className="gauge-right-panel">
                <h4>Score Breakdown</h4>
                <div className="breakdown-grid">
                    <ScoreItem label="Health Spending" score={scores.healthSpending} weight={25} />
                    <ScoreItem label="Federal Debt" score={scores.federalDebt} weight={20} />
                    <ScoreItem label="Household Burden" score={scores.householdBurden} weight={20} />
                    <ScoreItem label="Coverage & Access" score={scores.coverage} weight={15} />
                    <ScoreItem label="Provider Stability" score={scores.providerStability} weight={10} />
                    <ScoreItem label="Workforce" score={scores.workforce} weight={10} />
                </div>
            </div>
        </div>
    );
}

/**
 * Individual score item
 */
function ScoreItem({ label, score, weight }) {
    const color = getScoreColor(score);
    const percentage = (score / 10) * 100;

    return (
        <div className="score-item">
            <div className="score-item-header">
                <span className="score-label">{label}</span>
                <span className="score-value" style={{ color }}>
                    {score.toFixed(1)}
                </span>
            </div>
            <div className="score-bar-container">
                <div
                    className="score-bar"
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: color
                    }}
                />
            </div>
            <div className="score-weight">{weight}% weight</div>
        </div>
    );
}
